/* eslint-disable camelcase, no-param-reassign, max-lines-per-function */

import { productItemsPluralizer } from "common/product-items-pluralizer";
import getVariantInfo from "common/jiffy/get-variant";
import { track } from "jiffy-analytics";
import useSvg from "common/utils/ui/use_svg";

const SELECTORS = {
  CONTAINER: ".js-product-details-delivery-dates-container",
  SHIPMENTS_LIST: ".js-product-details-delivery-dates-shipments",
  CONTINUE_SHOPPING_MODAL_SHIPMENTS_LIST: ".js-continue-shopping-modal-delivery-dates-shipments",
  DELIVERY_DATES: ".js-continue-shopping-modal-delivery-dates",
  CART_DELIVERY_DATE: ".js-product-details-delivery-dates-cart-value",
  DIRECT_DELIVERY_DATE: ".js-product-details-delivery-dates-direct-value",
  J1ST_ICON: ".product-details-delivery-date__icon-wrapper",
  // Decoration Classes
  DATE_HIGHLIGHT: "product-details-quantity-form-row__actual-price--highlight",
  // Mixpanel
  ACTIVE_COLOR: "#active-color-name",
  PRODUCT_DETAILS_VARIANT_FORM: "#product-details-variants-form"
};

const jLogo = `
   <span class="product-details-delivery-date__icon-wrapper">
      ${useSvg("j-logo", { class: "product-details-delivery-date__icon" })}
    </span>
`;

// Getters
const getIsBuyItAgainStatus = () => !!document.getElementById("js-buy-it-again-modal");

const addHighLight = $elm => {
  $elm.classList.add(SELECTORS.DATE_HIGHLIGHT);
  setTimeout(() => {
    $elm.classList.remove(SELECTORS.DATE_HIGHLIGHT);
  }, 500);
};

const insertDate = (dates, $date) => {
  $date.innerHTML = dates.value_full;
  if (dates.shipments) {
    const deliveryDate = new Date(Math.min(...dates.shipments.map(s => new Date(s.full_date))))
      .toISOString()
      .slice(0, 10);
    $date.dataset.deliveryDate = deliveryDate;
  }
  const $parent = $date.parentNode;
  const $icon = $parent.querySelector(SELECTORS.J1ST_ICON);
  if (dates.show_logo && $icon !== $parent.firstElementChild) {
    $parent.insertAdjacentHTML("afterbegin", jLogo);
  }
  if (!dates.show_logo && $icon === $parent.firstElementChild) {
    $parent.removeChild($icon);
  }

  addHighLight($date);
};

const insertDeliveryDates = ({ cart_dates, composition_dates }) => {
  const $shipmentsLists = document.querySelectorAll(SELECTORS.SHIPMENTS_LIST);
  const $continueShoppingModalShipmentsList = document.querySelector(
    SELECTORS.CONTINUE_SHOPPING_MODAL_SHIPMENTS_LIST
  );
  const $cartDeliveryDates = document.querySelectorAll(SELECTORS.CART_DELIVERY_DATE);
  const $directDeliveryDates = document.querySelectorAll(SELECTORS.DIRECT_DELIVERY_DATE);
  const shipmentsForModal = cart_dates.shipments || composition_dates.shipments;

  if ("only_cart_shipments" in cart_dates) {
    const shipments = cart_dates.only_cart_shipments
      .map(
        shipment => `<dl>
            <dt>${shipment.show_logo ? jLogo : ""}${shipment.date}</dt>
            <dd>${productItemsPluralizer(shipment.items)}</dd>
          </dl>`
      )
      .join("");
    $shipmentsLists.forEach($list => {
      $list.innerHTML = shipments;
    });
  }

  if (shipmentsForModal) {
    document.querySelector(SELECTORS.DELIVERY_DATES).classList.remove("hidden");
    $continueShoppingModalShipmentsList.innerHTML = shipmentsForModal
      .map(({ date }) => `<div class="continue-shopping-modal__delivery-dates-item">${date}</div>`)
      .join("");
  }

  $cartDeliveryDates.forEach(insertDate.bind(this, cart_dates));
  $directDeliveryDates.forEach(insertDate.bind(this, composition_dates));
};

const deliveryTimeLeftInMinutes = compositionDates => {
  const getCutoffTime = new Date(compositionDates.timeleft).getTime();
  if (Number.isNaN(getCutoffTime)) return 0;

  const currentTime = Date.now();

  return Math.floor((getCutoffTime - currentTime) / 1000 / 60);
};

export const trackProductViewed = async ({
  cart_dates: cartDates,
  composition_dates: compositionDates,
  mixpanel
}) => {
  const activeColor = $(SELECTORS.ACTIVE_COLOR).val();
  const firstVariantId = $(
    `${SELECTORS.PRODUCT_DETAILS_VARIANT_FORM} input[data-color="${activeColor}"]`
  )
    .first()
    .data("variant-id");
  const variant = await getVariantInfo(firstVariantId);
  let compositionReferrer;

  if (window.localStorage.getItem("compositionReferrer")) {
    compositionReferrer = window.localStorage.getItem("compositionReferrer");
    window.localStorage.removeItem("compositionReferrer");
  } else {
    const urlParams = new URLSearchParams(window.location.search);
    const section = window.localStorage.getItem("pdpReferrerSection") || urlParams.get("section");
    compositionReferrer = section ? `${document.referrer}#${section}` : document.referrer;
    window.localStorage.removeItem("pdpReferrerSection");
  }

  let minDeliverySeparate = null;
  let maxDeliverySeparate = null;

  if (compositionDates.shipments && compositionDates.shipments.length > 0) {
    minDeliverySeparate = compositionDates.shipments[0].full_date;
    maxDeliverySeparate =
      compositionDates.shipments[compositionDates.shipments.length - 1].full_date;
  }

  const productViewedPayload = {
    brand: variant.product.brand,
    product_category: variant.product.adwords_category,
    product: variant.product.name,
    spree_composition_id: variant.compositionId,
    composition_inventory_rank: variant.compositionInventoryRank,
    composition: activeColor,
    composition_referrer: compositionReferrer,
    is_logged_in: variant.isLoggedIn,
    fulfillment_group: variant.fulfillmentGroup,
    min_delivery_separate: minDeliverySeparate,
    max_delivery_separate: maxDeliverySeparate,
    buy_it_again: getIsBuyItAgainStatus(),
    business_line: variant.product.business_line,
    delivery_date_considered_for: deliveryTimeLeftInMinutes(compositionDates)
  };

  const { deliveryDate: cartDeliveryDate } = cartDates;
  const { deliveryDate: directDeliveryDate } = compositionDates;

  if (cartDeliveryDate || directDeliveryDate) {
    const { delivery_date_considered_for: deliveryDateConsideredFor } = mixpanel;

    track("product_viewed", {
      ...productViewedPayload,
      with_cart_delivery_date: cartDeliveryDate,
      separately_shipped_delivery_date: directDeliveryDate,
      delivery_date_considered_for: deliveryDateConsideredFor
    });
  } else {
    track("product_viewed", productViewedPayload);
  }
};

export default insertDeliveryDates;
